import { cancelRequest, createRequestCoordinator } from "../core/request-coordinator.js";
import {
  canCancel,
  isTerminalRunState,
  parseEventPayload,
  resolveRunState,
  runStateFromEvent,
  sortDecisionsAscending
} from "../domain/conversation.js";
import { clearTransientThreadMediaState } from "./transient-media.js";

export function createThreadComponentState() {
  return {
    thread: null,
    workspace: null,
    workspaceDiagnostics: null,
    threadDiagnostics: null,
    turns: [],
    decisionsByTurnID: {},
    eventsByTurnID: {},
    eventSeqsByTurnID: {},
    streamState: "idle",
    notFound: false,
    composerDraft: "",
    composerCapability: "responses",
    composerRouteOverride: "",
    composerModelOverride: "",
    composerOverrideOpen: false,
    composerContextRefs: [],
    composerContextPanelOpen: false,
    contextItems: [],
    contextItemsLoadedThreadID: "",
    contextItemsLoading: false,
    contextItemsError: "",
    contextItemFilterOpen: false,
    contextItemFilterText: "",
    contextItemDraftTitle: "",
    contextItemDraftText: "",
    contextItemDraftContentType: "text/plain",
    contextItemUploadFilename: "",
    contextItemDeleteConfirmID: "",
    contextItemPreviewID: "",
    contextItemPreviewLoading: false,
    contextItemPreviewError: "",
    pendingAttachments: [],
    captureState: {
      kind: "",
      status: "idle",
      source: ""
    },
    decisionDrafts: {},
    expandedProgressIDs: new Set(),
    submitting: false,
    live: {
      streamAbort: null,
      snapshotTimer: null,
      pollTimer: null
    },
    requests: {
      snapshot: createRequestCoordinator()
    }
  };
}

export function cancelThreadComponentRequests(state) {
  if (state?.requests?.snapshot) {
    cancelRequest(state.requests.snapshot);
  }
}

export function resetThreadComponentState(state, options = {}) {
  revokeThreadMediaURLs(state, options.preserveObjectURLs || null);
  if (options.preserveTransientMedia !== true) {
    clearTransientThreadMediaState(state);
  }
  if (options.cancelRequests !== false) {
    cancelThreadComponentRequests(state);
  }
  const next = createThreadComponentState();
  for (const key of Object.keys(state)) {
    delete state[key];
  }
  Object.assign(state, next);
}

export function activeThreadTurnFromState(state) {
  for (let index = state.turns.length - 1; index >= 0; index -= 1) {
    if (canCancel(state.turns[index].state)) {
      return state.turns[index];
    }
  }
  return null;
}

export function threadSendBlockReason(state, threadItem, activeTurn) {
  if (!threadItem) {
    return "select a thread first";
  }
  if (state.captureState?.status === "recording") {
    return "finish microphone capture first";
  }
  if ((state.pendingAttachments || []).some((item) => item?.uploading)) {
    return "wait for attachment uploads to finish";
  }
  if (state.submitting) {
    return "queueing turn...";
  }
  if (activeTurn && activeTurn.wait_state === "awaiting_decision") {
    return "resolve the pending decision above to continue";
  }
  if (activeTurn && canCancel(activeTurn.state)) {
    return "wait for the active turn to finish";
  }
  const threadState = String(threadItem.state || "").trim().toLowerCase();
  if (threadState === "recovery_required") {
    return "thread requires recovery";
  }
  if (threadState === "archived" || threadState === "merged") {
    return "thread is archived";
  }
  if (threadState === "merging" || threadState === "awaiting_remote_merge") {
    return `thread is ${threadState.replaceAll("_", " ")}`;
  }
  if (threadState !== "" && threadState !== "open" && threadState !== "ready_to_merge" && threadState !== "merge_conflicted") {
    return `thread is ${threadState.replaceAll("_", " ")}`;
  }
  return "";
}

function revokeThreadMediaURLs(state, preservedURLs = null) {
  const seen = new Set();
  for (const item of state?.pendingAttachments || []) {
    revokeObjectURL(item?.object_url, seen, preservedURLs);
  }
  for (const ref of state?.composerContextRefs || []) {
    revokeObjectURL(ref?.attachment?.object_url, seen, preservedURLs);
  }
  for (const ref of state?.thread?.pinned_context_refs || []) {
    revokeObjectURL(ref?.attachment?.object_url, seen, preservedURLs);
  }
  for (const turn of state?.turns || []) {
    for (const part of turn?.input_parts || []) {
      revokeObjectURL(part?.attachment?.object_url, seen, preservedURLs);
    }
    for (const part of turn?.output_parts || []) {
      revokeObjectURL(part?.attachment?.object_url, seen, preservedURLs);
    }
  }
}

function revokeObjectURL(value, seen, preservedURLs) {
  const objectURL = String(value || "").trim();
  if (!objectURL || seen.has(objectURL) || preservedURLs?.has(objectURL)) {
    return;
  }
  seen.add(objectURL);
  URL.revokeObjectURL(objectURL);
}

export function latestPendingDecisionForTurn(state, turnID) {
  return sortDecisionsAscending((state.decisionsByTurnID[turnID] || []).filter((item) => item.state === "pending")).slice(-1)[0] || null;
}

export function lookupDecision(state, decisionID) {
  for (const items of Object.values(state.decisionsByTurnID || {})) {
    const match = items.find((item) => item.id === decisionID);
    if (match) {
      return match;
    }
  }
  return null;
}

export function lookupDecisionTurn(state, turnID) {
  const normalizedTurnID = String(turnID || "");
  if (!normalizedTurnID) {
    return null;
  }
  return state.turns.find((current) => current.id === normalizedTurnID) || null;
}

export function threadProgressLines(events) {
  return events
    .filter((item) => item.event_type === "turn.progress")
    .map((item) => parseEventPayload(item.payload_json))
    .map((payload) => {
      const message = typeof payload.message === "string" ? payload.message.trim() : "";
      const stage = typeof payload.stage === "string" ? payload.stage.trim() : "";
      if (message && stage) {
        return `${stage}: ${message}`;
      }
      return message || stage;
    })
    .filter(Boolean);
}

export function threadTraceEntries(events) {
  return events
    .filter((item) => item.event_type === "turn.trace")
    .map((item) => parseEventPayload(item.payload_json))
    .filter((payload) => payload && typeof payload.event_type === "string")
    .map((payload) => ({
      eventType: payload.event_type,
      body: JSON.stringify(payload.payload || {}, null, 2)
    }));
}

export function threadTurnStatusText(runState) {
  switch (runState) {
  case "queued":
    return "Queued";
  case "running":
    return "Streaming";
  case "succeeded":
    return "Completed";
  case "applied_with_warning":
    return "Applied with warning";
  case "failed":
    return "Failed";
  case "canceled":
    return "Canceled";
  default:
    return runState || "Unknown";
  }
}

export function latestThreadSeq(state, turnID) {
  const events = state.eventsByTurnID[turnID] || [];
  return events.length === 0 ? 0 : events[events.length - 1].seq || 0;
}

export function syncTurnStateFromEvent(state, turnID, event) {
  const nextState = runStateFromEvent(event);
  if (!turnID || !nextState) {
    return;
  }
  state.turns = state.turns.map((item) => item.id === turnID ? { ...item, state: resolveRunState(item.state, nextState) } : item);
  if (!state.thread) {
    return;
  }
  if (nextState === "running") {
    state.thread = { ...state.thread, state: "running" };
    return;
  }
  if (isTerminalRunState(nextState) && state.thread.state === "running") {
    state.thread = { ...state.thread, state: threadStateFromMergeState(state.thread.merge_state) };
  }
}

export function mergeThreadRefreshState(state, snapshot) {
  state.thread = mergeThreadRecord(state.thread, snapshot.thread);
  state.workspace = snapshot.workspace;
  state.workspaceDiagnostics = snapshot.workspaceDiagnostics;
  state.threadDiagnostics = snapshot.threadDiagnostics;
  state.turns = mergeThreadTurns(state.turns, snapshot.turns);
  state.decisionsByTurnID = snapshot.decisionsByTurnID;
  mergeEventsByTurnID(state, snapshot.eventsByTurnID, snapshot.eventSeqsByTurnID);
  if (state.thread) {
    const hasActiveTurn = state.turns.some((item) => canCancel(String(item?.state || "").trim()));
    if (hasActiveTurn) {
      state.thread = { ...state.thread, state: "running" };
    } else if (state.thread.state === "running") {
      state.thread = { ...state.thread, state: threadStateFromMergeState(state.thread.merge_state) };
    }
  }
  state.notFound = false;
}

function mergeThreadRecord(currentThread, nextThread) {
  if (!nextThread || !nextThread.id) {
    return currentThread;
  }
  if (!currentThread || currentThread.id !== nextThread.id) {
    return nextThread;
  }
  return {
    ...currentThread,
    ...nextThread,
    state: String(nextThread.state || currentThread.state || "").trim()
  };
}

function mergeThreadTurns(currentTurns, nextTurns) {
  const merged = new Map();
  for (const item of currentTurns || []) {
    if (item?.id) {
      merged.set(item.id, item);
    }
  }
  for (const item of nextTurns || []) {
    if (!item?.id) {
      continue;
    }
    const existing = merged.get(item.id);
    if (!existing) {
      merged.set(item.id, item);
      continue;
    }
    merged.set(item.id, {
      ...existing,
      ...item,
      state: resolveRunState(existing.state, item.state)
    });
  }
  return [...merged.values()].sort((left, right) => new Date(left.created_at || 0) - new Date(right.created_at || 0));
}

function mergeEventsByTurnID(state, snapshotEventsByTurnID, snapshotEventSeqsByTurnID) {
  const nextEventsByTurnID = {};
  const nextEventSeqsByTurnID = {};
  const turnIDs = new Set([
    ...Object.keys(state.eventsByTurnID || {}),
    ...Object.keys(snapshotEventsByTurnID || {})
  ]);
  for (const turnID of turnIDs) {
    const mergedEvents = mergeTurnEvents(
      state.eventsByTurnID?.[turnID] || [],
      snapshotEventsByTurnID?.[turnID] || []
    );
    nextEventsByTurnID[turnID] = mergedEvents;
    nextEventSeqsByTurnID[turnID] = new Set(mergedEvents.map((item) => item.seq));
    if (snapshotEventSeqsByTurnID?.[turnID]) {
      for (const seq of snapshotEventSeqsByTurnID[turnID]) {
        nextEventSeqsByTurnID[turnID].add(seq);
      }
    }
  }
  state.eventsByTurnID = nextEventsByTurnID;
  state.eventSeqsByTurnID = nextEventSeqsByTurnID;
}

function mergeTurnEvents(currentEvents, nextEvents) {
  const merged = new Map();
  for (const item of currentEvents || []) {
    if (item && typeof item.seq === "number") {
      merged.set(item.seq, item);
    }
  }
  for (const item of nextEvents || []) {
    if (item && typeof item.seq === "number") {
      merged.set(item.seq, item);
    }
  }
  return [...merged.values()].sort((left, right) => left.seq - right.seq);
}

function threadStateFromMergeState(value) {
  switch (String(value || "").trim()) {
  case "ready_to_merge":
    return "ready_to_merge";
  case "merging":
    return "merging";
  case "awaiting_remote_merge":
    return "awaiting_remote_merge";
  case "merge_conflicted":
    return "merge_conflicted";
  case "merged":
    return "merged";
  case "archived":
    return "archived";
  default:
    return "open";
  }
}
